import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { environmentName } from '../common/EnvironmentBanner'
import { useAuth } from '../../hooks/useAuth'
import InstallAppButton from '../common/InstallAppButton.jsx'
import LanguagePicker from '../common/LanguagePicker.jsx'
import api from '../../services/api'
import { useBranding, brandHex, brandTextOn } from '../../hooks/useBranding.jsx'
import { useTranslation } from 'react-i18next'

function Login() {
  const { t: tch } = useTranslation('chrome')
  const navigate = useNavigate()
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setError] = useState('')

  // A SIGN-IN THAT FAILED HAS TO SAY SO.
  //
  // The server sends somebody here with ?sso_error=1 when their company's sign-in did not
  // complete, and until now nothing read it: they landed on an ordinary login page with no
  // hint that anything had gone wrong, and their password no longer works either. The reason
  // itself stays in our log rather than on screen — it names issuers and domains, and the
  // person reading this is somebody's employee who clicked a button.
  const [ssoFailed, setSsoFailed] = useState(false)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (!params.get('sso_error')) return
    setSsoFailed(true)
    // Taken out of the address so a refresh does not keep re-announcing an old failure.
    params.delete('sso_error')
    const rest = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''))
  }, [])
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  // Which door this person comes through, answered as they leave the email box. Null until
  // asked; an object once we know they belong somewhere that signs them in itself.
  const [managed, setManaged] = useState(null)

  // Asked on blur rather than on every keystroke: the endpoint is rate-limited, and half an
  // address answers nothing useful anyway.
  const checkDoor = async () => {
    const email = (formData.email || '').trim()
    if (!email.includes('@')) { setManaged(null); return }
    try {
      const { data } = await api.post('/auth/identify', { email })
      setManaged(data?.managed ? data : null)
    } catch {
      // A failed lookup must leave the ordinary password box in place. Never a dead end.
      setManaged(null)
    }
  }
  const { login, isAuthenticated } = useAuth()
  const branding = useBranding()

  // Demo credentials panel is for our internal team only — hidden from real users.
  // Visiting /login?demo=1 once unlocks it for that browser (persists), so during the
  // testing phase the team doesn't need the param on every login. Real users who never
  // open the demo link never see it.
  const [demoUnlocked, setDemoUnlocked] = useState(false)
  useEffect(() => {
    // NOT ON PRODUCTION. Ever, by any route.
    //
    // ?demo=1 was never a lock — it writes a flag to localStorage, and the person typing the
    // URL is the person it is meant to keep out. On the live site this panel listed Admin,
    // Project Manager, Finance and Consultant with a click-to-fill password, which made
    // one-click Admin available to anyone who guessed a query string. The passwords were real:
    // four active production accounts verified against "password".
    //
    // environmentName() reads the hostname, so this cannot be switched on by a visitor, a
    // stale localStorage entry, or a build flag somebody forgot to set. Production has no
    // "staging" in its address and gets null, and null means the panel does not exist.
    // On staging it is simply there — no ?demo=1, no localStorage flag to carry around.
    // The key never protected anything anyway: it was a query string, and the person typing it
    // was the person it was meant to keep out.
    //
    // What DOES protect it is the line above. environmentName() reads the hostname, so on
    // production this returns before anything below runs and the panel cannot exist there by
    // any route.
    if (!environmentName()) return
    setDemoUnlocked(true)
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      // A deep-linked email (e.g. "you're assigned to a project") that bounced through
      // here while logged out leaves its intended destination in sessionStorage — send
      // the user straight there instead of stranding them on the generic dashboard.
      const redirect = sessionStorage.getItem('postLoginRedirect')
      sessionStorage.removeItem('postLoginRedirect')
      navigate(redirect || '/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const demoCredentials = [
    // Platform oversight — sees every product, manages the product list and who may open what.
    // Deliberately listed first and styled apart: it is the only account above ProTrack itself.
    { role: 'Super Admin', email: 'devops.internal@3pillarsdata.com', password: 'password', color: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', text: 'text-amber-800' },
    { role: 'Admin', email: 'admin@3pillarsdata.com', password: 'password', color: 'from-purple-500 to-indigo-600', bg: 'bg-purple-50', text: 'text-purple-700' },
    // Funder role is paused for now — only Admin, PM, Finance, Consultant are in use.
    { role: 'Project Manager', email: 'maha@3pillarsdata.com', password: 'password', color: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50', text: 'text-blue-700' },
    { role: 'Finance', email: 'dhiraj@3pillarsdata.com', password: 'password', color: 'from-yellow-400 to-orange-500', bg: 'bg-yellow-50', text: 'text-yellow-800' },
    { role: 'Consultant', email: 'abhishek@3pillarsdata.com', password: '12345678', color: 'from-indigo-500 to-violet-600', bg: 'bg-indigo-50', text: 'text-indigo-700' },
    { role: 'HR', email: 'dhirajmattur@gmail.com', password: 'password', color: 'from-teal-500 to-emerald-600', bg: 'bg-teal-50', text: 'text-teal-700' }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const result = await login(formData.email, formData.password)
    if (!result.success) setError(result.error)
    setLoading(false)
  }

  // Editing the address invalidates what we were told about the previous one — otherwise the
  // button keeps offering a company the typed address no longer belongs to.
  const onEmailChange = (e) => { setManaged(null); handleChange(e) }

  const handleDemoLogin = (credentials) => {
    setFormData({
      email: credentials.email,
      password: credentials.password
    })
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-[30%] -left-[10%] w-[70%] h-[70%] rounded-full bg-blue-100 blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-[30%] -right-[10%] w-[70%] h-[70%] rounded-full bg-purple-100 blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      </div>

      <div className={`w-full z-10 grid grid-cols-1 gap-8 ${demoUnlocked ? 'max-w-6xl lg:grid-cols-2' : 'max-w-md'}`}>

        {/* Login Section */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20 flex flex-col justify-center">
          {/* Before sign-in, not after. Somebody who cannot read this page cannot reach a
              settings screen to fix it — the choice has to be reachable from where they are
              stuck. Saved to their record on the next request that has a session. */}
          <div className="flex justify-end -mt-2 mb-2">
            <LanguagePicker compact />
          </div>
          {/* Whose sign-in this is. Shown only for a company served at this address — on our own
              address there is nothing to say that the page does not already say. */}
          {branding.organisation && (
            <div className="text-center mb-6">
              <img
                src={branding.logoUrl}
                alt={branding.productName}
                className="h-12 object-contain mx-auto"
                style={{ maxWidth: '200px' }}
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
              <p className="mt-3 text-sm font-medium text-gray-700">{branding.productName}</p>
            </div>
          )}
          {ssoFailed && (
            <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">Signing in through your company did not complete.</p>
              <p className="mt-1">
                Nothing is wrong with your account. Please try again, and if it happens a second
                time, ask whoever manages sign-in at your organisation to contact us — we have
                the details of what failed.
              </p>
            </div>
          )}

          <div className="text-center mb-10">
            <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">
              Welcome <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">{tch('back')}</span>
            </h1>
            <p className="mt-2 text-sm text-gray-600">{tch('sign_in_subtitle')}</p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 round shadow-sm" role="alert">
                <p className="font-bold">{tch('error')}</p>
                <p>{error}</p>
              </div>
            )}

            {/* The way in, for somebody whose account lives in a connected product. Shown ONLY
                to that person and naming ONLY their own product — never a list of everything
                that exists, which would be clutter for the ProTrack users who are most of the
                people here, and an advertisement of the registry to everyone else.

                The name and the destination both come from that product's registry row, so a
                second product needs no change to this file. */}
            <InputField
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={onEmailChange}
              onBlur={checkDoor}
              required
              placeholder={tch('ph_enter_email')}
            />

            {/* Their door, INSTEAD OF the password box — not beside it. A password field shown
                to somebody whose password lives somewhere else is an invitation to try one that
                cannot work, and then to conclude the account is broken. */}
            {managed ? (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4">
                <p className="text-sm text-indigo-900">
                  {managed.kind === 'organisation'
                    ? <><strong>{managed.organisation_name}</strong> signs in through their own system.</>
                    : <>Your account lives in <strong>{managed.product_name}</strong>.</>}
                </p>
                <a
                  href={managed.signin_url}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  Continue with {managed.organisation_name || managed.product_name} →
                </a>
                {/* A way back, because a wrong guess must never trap somebody who does have a
                    password here. */}
                <button
                  type="button"
                  onClick={() => setManaged(null)}
                  className="mt-2 w-full text-xs text-indigo-700/70 hover:text-indigo-900"
                >
                  Use a password instead
                </button>
              </div>
            ) : (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 ml-1">{tch('password')}</label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder={tch('ph_enter_password')}
                  className="appearance-none block w-full px-4 py-3 bg-white text-gray-900 rounded-lg border border-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ease-in-out shadow-sm hover:border-gray-300 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.414-1.414A9 9 0 0010 14a9 9 0 004.745-1.343l-1.528-1.528A6.983 6.983 0 0110 12a7 7 0 01-6.945-6.52L3.707 2.293zM6.343 4.929l1.698 1.698A4.004 4.004 0 0110 8c1.105 0 2.105.447 2.828 1.172l1.328 1.328A9.006 9.006 0 0010 3a9 9 0 00-3.657.772z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            )}

            {/* The submit button belongs to the password route only. Left visible alongside the
                organisation button it would offer to sign in with an empty password. */}
            {!managed && (
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-xl hover:scale-[1.01] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                // The first screen a customer's staff ever see, so it is the one that most needs
                // to be theirs. backgroundImage is cleared as well as the colour set, because the
                // classes above paint a gradient and a background colour alone would sit behind it
                // and never show. Ours is untouched when no colour was chosen.
                style={brandHex(branding.primaryColour)
                  ? {
                    backgroundImage: 'none',
                    backgroundColor: branding.primaryColour,
                    // The class above sets white text, which vanishes on a pastel. Their colour
                    // is their choice; whether the writing on it can be read is ours.
                    color: brandTextOn(branding.primaryColour),
                  }
                  : undefined}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : 'Sign In'}
              </button>
            </div>
            )}

            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="font-semibold text-blue-600 hover:text-blue-500 hover:underline">
                  Sign up
                </Link>
              </p>
              <div className="mt-4">
                <InstallAppButton
                  label="Install ProTrack on your Desktop"
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-indigo-700 border border-indigo-200 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors"
                />
              </div>
            </div>
          </form>
        </div>

        {/* Demo Roles Section — internal team only (unlock via /login?demo=1) */}
        {demoUnlocked && (
        <div className="bg-white/60 backdrop-blur-md rounded-2xl shadow-xl p-8 border border-white/20 flex flex-col">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{tch('demo_access')}</h3>
            <p className="text-sm text-gray-600">
              Click a role to auto-fill credentials and explore the platform instantly.
            </p>
          </div>

          <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar">
            {demoCredentials.map((cred, index) => (
              <div
                key={index}
                onClick={() => handleDemoLogin(cred)}
                className="group relative bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md cursor-pointer transition-all duration-200 hover:-translate-y-1 overflow-hidden"
              >
                <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${cred.color}`}></div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${cred.bg} ${cred.text}`}>
                    {cred.role}
                  </span>
                  <div className="text-right">
                    <p className="text-xs font-mono text-gray-500">{cred.email}</p>
                    <p className="text-xs font-mono text-gray-400">******</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-100 group-hover:border-blue-200 transition-colors">
                  {cred.role === 'Super Admin' && 'Every product, the product list & who may open what'}
                  {cred.role === 'Admin' && 'Full system control & user management'}
                  {cred.role === 'Funder' && 'Investment tracking & budget approval'}
                  {cred.role === 'Project Manager' && 'Task oversight & team coordination'}
                  {cred.role === 'Finance' && 'Budget reporting & expense auditing'}
                  {cred.role === 'Consultant' && 'Advisory insights & impact tracking'}
                </p>
              </div>
            ))}
          </div>
        </div>
        )}

      </div>
    </div>
  )
}

const InputField = ({ label, name, type = "text", value, onChange, onBlur, required = false, placeholder = "" }) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 ml-1">{label}</label>
    <input
      name={name}
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      className="appearance-none block w-full px-4 py-3 bg-white text-gray-900 rounded-lg border border-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ease-in-out shadow-sm hover:border-gray-300"
      onChange={onChange}
      onBlur={onBlur}
    />
  </div>
);

export default Login
